import { Link, useLocation, useParams } from '@tanstack/react-router'
import { LogIn, MousePointer2 } from 'lucide-react'
import { useTranslations } from 'use-intl'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { isFeatureKey } from '@/features/solution-page/data/features'
import { seedConfiguratorApps } from '@/features/solution-configurator/lib/deep-link'
import { TrialFormDialog } from '@/features/trial-form/components/trial-form-dialog'
import type { PageType } from '@/lib/analytics/events'
import {
  trackBookDemo,
  trackFeatureCtaClick,
  trackTrialClick,
} from '@/lib/analytics/events'

type Props = React.ComponentProps<typeof Button> & {
  icon?: React.ComponentType<{ className?: string }>
  // Attribution for the trial funnel (trial_click/form_start/submit): labels
  // which entry point this button is (hero, header, footer, faq…). Optional —
  // the funnel still fires without it, just with no source breakdown.
  source?: string
}

// Best-effort page_type from the current path for CTA attribution.
function pageTypeFromPath(pathname: string): PageType {
  if (/\/pricing\/?$/.test(pathname)) return 'pricing'
  if (/\/about\/?$/.test(pathname)) return 'about'
  if (/\/solutions\/[^/]+\/[^/]+\/?$/.test(pathname)) return 'solution_feature'
  if (/\/solutions\//.test(pathname)) return 'solution'
  if (/\/blog\//.test(pathname)) return 'blog_article'
  if (/\/tools\//.test(pathname)) return 'tool'
  return 'home'
}

function BookDemoButton({ className, children, icon: Icon = MousePointer2, source, ...props }: Props) {
  const t = useTranslations()
  const { pathname } = useLocation()
  const { locale } = useParams({ from: '/$locale' })

  // On a people-security feature page the CTA becomes "Obtenir mon devis",
  // deep-linking into the configurator with that page's app pre-selected.
  const feature = pathname.match(
    /\/solutions\/people-security\/([^/]+)\/?$/,
  )?.[1]
  if (feature && isFeatureKey(feature)) {
    return (
      <Button asChild className={cn(className)} {...props}>
        <Link
          to="/$locale/tools/solution-configurator"
          params={{ locale }}
          onClick={() => {
            seedConfiguratorApps([feature])
            trackFeatureCtaClick({ feature })
          }}
        >
          <Icon className="fill-current" />
          {t('cta.startQuote')}
        </Link>
      </Button>
    )
  }

  // On the solution landing pages the demo CTA instead scrolls to that page's
  // app configurator (`#app-showcase`), overriding any per-section copy.
  if (
    pathname.endsWith('/solutions/people-security') ||
    pathname.endsWith('/solutions/property-security')
  ) {
    // Scoped smooth scroll (not global `scroll-behavior`, which would also
    // animate route-change scroll-to-top). Honours prefers-reduced-motion.
    const slideToShowcase: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
      const el = document.getElementById('app-showcase')
      if (!el) return
      e.preventDefault()
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' })
      history.replaceState(null, '', '#app-showcase')
      trackBookDemo({ pageType: 'solution', section: 'cta' })
    }
    return (
      <Button asChild className={cn(className)} {...props}>
        <a href="#app-showcase" onClick={slideToShowcase}>
          <Icon className="fill-current" />
          {t('cta.composeApp')}
        </a>
      </Button>
    )
  }

  // Everywhere else, "Essayer gratuitement" opens the company-name form.
  return (
    <TrialFormDialog
      source={source}
      trigger={
        <Button
          className={cn(className)}
          {...props}
          onClick={() => {
            trackBookDemo({ pageType: pageTypeFromPath(pathname), section: 'cta' })
            trackTrialClick({ source })
          }}
        >
          <Icon className="fill-current" />
          {/* `children` overrides the copy when a section wants its own
              wording, without duplicating the icon. */}
          {children ?? t('cta.bookDemo')}
        </Button>
      }
    />
  )
}

function ClientAreaButton({ className, ...props }: Props) {
  const t = useTranslations()
  return (
    <Button variant="outline" asChild className={cn(className)} {...props}>
      <a href="https://apps.countact.fr">
        {t('cta.clientArea')}
        <LogIn />
      </a>
    </Button>
  )
}

export { BookDemoButton, ClientAreaButton }
